import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { TransportUnitsClient } from "@/features/resources/TransportUnitsClient";
import {
  listTransportUnits,
  resourcesKeys,
  type EquipmentDetail,
  type ResourcesSearch,
  type TransportUnitListItem,
  type VehicleDetail,
} from "@/features/resources/resources.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/transport-units/")({
  // Reading is tms_transport_units (Resources → Transportmiddelen, S31);
  // tms_resources_write makes and manages the units (S34) — in the screen, and on the API regardless.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_transport_units") && !hasModule(context.session, "tms_resources_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): ResourcesSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      q: str(search.q),
      type: str(search.type),
      branch: str(search.branch),
      isActive: str(search.isActive),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: TransportUnitsScreen,
});

function TransportUnitsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();
  const canManage = session.roles.includes("admin") || hasModule(session, "tms_resources_write");

  // A unit card's back link returns to the list as it was left.
  useEffect(() => {
    rememberListSearch("/transport-units", search);
  }, [search]);

  const query = useQuery({
    queryKey: resourcesKeys.unitList(search),
    queryFn: () => listTransportUnits(search),
    placeholderData: keepPreviousData,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const openUnit = useCallback(
    (unit: TransportUnitListItem) => {
      if (unit.unitType === "vehicle") navigate({ to: "/transport-units/vehicles/$id", params: { id: unit.id } });
      else navigate({ to: "/transport-units/equipment/$id", params: { id: unit.id } });
    },
    [navigate],
  );

  const handleCreated = useCallback(
    (unit: VehicleDetail | EquipmentDetail) => {
      void queryClient.invalidateQueries({ queryKey: resourcesKeys.units, refetchType: "none" });
      if (unit.unitType === "vehicle") {
        queryClient.setQueryData(resourcesKeys.vehicle(unit.id), unit);
        navigate({ to: "/transport-units/vehicles/$id", params: { id: unit.id } });
      } else {
        queryClient.setQueryData(resourcesKeys.equipment(unit.id), unit);
        navigate({ to: "/transport-units/equipment/$id", params: { id: unit.id } });
      }
    },
    [navigate, queryClient],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Transport Units">
      <TransportUnitsClient
        items={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        updateParams={updateParams}
        onOpen={openUnit}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        canManage={canManage}
        onCreated={handleCreated}
        userId={session.userId}
      />
    </div>
  );
}
